import { isListingCardModel } from '@core/typeguards';
import { hasAnyIcons } from '@stories/Elements/Cards/ProductCard/ProductCard';
import { getActiveStars } from '@stories/Elements/Misc/RatingStars/RatingStars';
import React, { FunctionComponent, createContext, useContext, useMemo } from 'react';

interface ProductCardsState {
  mappings: Map<number, RowOptions>;
}

interface UseProductCardsProps {
  pageSize: number;
  products: ProductViewModel[];
  cards: ListingCardModel[];
  iconMapping: ProductViewModelIconMapping[];
  rowItems: number;
  cultureCode: string;
}

interface RowOptions {
  isNew: boolean;
  hasUnpublishedTag: boolean;
  hasSystemIcon: boolean;
  hasAttributeIcons: boolean;
  hasReviews: boolean;
  hasButton: boolean;
  longestTitle: string;
}

const ProductCardsContext = createContext({});

export const ProductCardsProvider: FunctionComponent<Required<UseProductCardsProps>> = (props) => {
  const { products, cards, rowItems, pageSize, iconMapping } = props;

  // Creates a tag mapping using rowIndex => hasTag
  const mappings = useMemo(() => {
    const map = new Map<number, RowOptions>();

    if (products.length === 0) {
      return map;
    }

    const totalPageSize = products.length < pageSize ? products.length : pageSize;

    const rows = Math.ceil(totalPageSize / rowItems);

    const allCards: (ProductViewModel | ListingCardModel)[] = [];

    for (let index = 0; index < totalPageSize; index++) {
      const product = products[index];
      const promoCard = cards.find((x) => x.position - 1 === index);

      if (promoCard) {
        allCards.push(promoCard);
      }

      if (product) {
        allCards.push(product);
      }
    }

    for (let rowIndex = 0; rowIndex < rows; rowIndex++) {
      let isNew = false;
      let hasTag = false;
      let hasUnpublishedTag = false;
      let hasSystemIcon = false;
      let hasAttributeIcons = false;
      let hasReviews = false;
      let hasButton = false;
      let longestTitle = '';

      for (let productIndex = 0; productIndex < rowItems; productIndex++) {
        const actualProductIndex = rowIndex * rowItems + productIndex;

        const card = allCards[actualProductIndex];

        if (!card) {
          continue;
        }

        if (isListingCardModel(card)) {
          map.set(rowIndex, {
            isNew,
            hasUnpublishedTag,
            hasSystemIcon,
            hasAttributeIcons,
            hasReviews,
            hasButton,
            longestTitle,
          });

          continue;
        } else {
          if (!card) {
            continue;
          }

          if (hasTag === false) {
            hasTag = !!card.tagText;
          }

          if (isNew === false) {
            isNew = card.isNew;
          }

          if (hasUnpublishedTag === false) {
            hasUnpublishedTag = !card.isPublished;
          }

          if (hasSystemIcon === false) {
            hasSystemIcon = !!card.systemLogo && !!card.systemLogo.whiteBg;
          }

          if (hasAttributeIcons === false) {
            hasAttributeIcons = hasAnyIcons(card, iconMapping);
          }

          if (hasReviews === false) {
            hasReviews = getActiveStars(card.averageReviewRating) > 0;
          }

          if (hasButton === false) {
            hasButton = card.isPublished;
          }

          if (card.title.length > longestTitle.length) {
            longestTitle = card.title;
          }
        }
      }

      map.set(rowIndex, {
        isNew,
        hasUnpublishedTag,
        hasSystemIcon,
        hasAttributeIcons,
        hasReviews,
        hasButton,
        longestTitle,
      });
    }

    return map;
  }, [products, cards, pageSize, rowItems]);

  const result: ProductCardsState = {
    mappings,
  };

  return <ProductCardsContext.Provider value={result}>{props.children}</ProductCardsContext.Provider>;
};

const useProductCards = (): ProductCardsState | undefined => {
  const productCards = useContext(ProductCardsContext);

  if (Object.keys(productCards).length === 0) {
    return undefined;
  }

  return productCards as ProductCardsState;
};

export default useProductCards;
